import type { QueryClient } from "@tanstack/react-query";
import type { NurseMonthlyLimitItem } from "@/api/nurses";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import type { NurseResponse } from "@/types/nurse-type";

export type InvalidateNurseCachesOptions = {
  readonly listGroupId?: string | null;
  readonly memberGroupId?: string | null;
  readonly nurseId?: string;
  readonly fullSchemaGroupId?: string | null;
  readonly year?: number | null;
  readonly month?: number | null;
  readonly includeMonthlyLimits?: boolean;
};

export async function invalidateNurseCaches(
  queryClient: QueryClient,
  options: InvalidateNurseCachesOptions
): Promise<void> {
  const tasks: Promise<unknown>[] = [
    queryClient.invalidateQueries({
      queryKey:
        options.year != null && options.month != null
          ? QUERY_KEYS.nurses.list(
              options.listGroupId,
              options.year,
              options.month
            )
          : QUERY_KEYS.nurses.listScope(options.listGroupId),
    }),
    queryClient.invalidateQueries({
      queryKey:
        options.year != null && options.month != null
          ? QUERY_KEYS.nurses.members(
              options.memberGroupId,
              options.year,
              options.month
            )
          : QUERY_KEYS.nurses.membersScope(options.memberGroupId),
    }),
  ];

  if (options.nurseId) {
    tasks.push(
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.nurses.detail(options.nurseId),
        refetchType: "all",
      })
    );
  }

  if (options.fullSchemaGroupId !== undefined) {
    tasks.push(
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.nurses.fullSchema(options.fullSchemaGroupId),
      })
    );
  }

  if (options.includeMonthlyLimits && options.nurseId) {
    tasks.push(
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.nurses.monthlyLimits(
          options.memberGroupId ?? options.listGroupId,
          options.nurseId
        ),
      })
    );
  }

  await Promise.all(tasks);
}

export function patchNurseNightLimits(
  previous: NurseResponse[] | undefined,
  changedItems: readonly NurseMonthlyLimitItem[]
): NurseResponse[] | undefined {
  if (!previous || changedItems.length === 0) return previous;

  const changedByNurseId = new Map(
    changedItems.map((item) => [item.nurse_id, item])
  );
  let changed = false;

  const next = previous.map((nurse) => {
    const item = changedByNurseId.get(nurse.nurse_id);
    if (!item) return nurse;
    changed = true;
    return {
      ...nurse,
      n_max: item.n_max ?? null,
      n_exact: item.n_exact ?? null,
    };
  });

  return changed ? next : previous;
}

export function patchNurseNightLimitsInListCaches(
  queryClient: QueryClient,
  changedItems: readonly NurseMonthlyLimitItem[]
): void {
  if (changedItems.length === 0) return;

  queryClient.setQueriesData<NurseResponse[] | undefined>(
    {
      queryKey: QUERY_KEYS.nurses.scope(),
      predicate: (query) => {
        const [root, scope] = query.queryKey;
        return (
          root === "NURSES" &&
          scope !== "members" &&
          scope !== "monthly-limits" &&
          scope !== "detail"
        );
      },
    },
    (previous) => patchNurseNightLimits(previous, changedItems)
  );
}
